import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Refresh as RefreshIcon, 
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  TransferWithinAStation as TransferIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

import { getBackendUrl } from '../config/urls';

const BACKEND_URL = getBackendUrl();

const AutoTransferMonitor = () => {
  const { token } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [addMonitorDialog, setAddMonitorDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);

  // Form states
  const [addMonitorForm, setAddMonitorForm] = useState({
    walletId: '',
    tokenAddress: '',
    tokenName: '',
    tokenSymbol: '',
    recipientAddress: '',
    minAmount: '1',
    maxAmount: '1000000',
    checkInterval: '5', // seconds
    enabled: true
  });

  // Settings states
  const [globalSettings, setGlobalSettings] = useState({
    globalCheckInterval: '3', // seconds
    maxRetries: '3',
    retryDelay: '10', // seconds
    useFlashbots: false,
    useRegularTransaction: true
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load wallets
      const walletsResponse = await fetch(`${BACKEND_URL}/api/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (walletsResponse.ok) {
        const walletsData = await walletsResponse.json();
        const delegatedWallets = walletsData.success ? 
          (walletsData.wallets || []).filter(wallet => wallet.isDelegated) : [];
        setWallets(delegatedWallets);
      }

      // Load auto-transfer monitors
      const monitorsResponse = await fetch(`${BACKEND_URL}/api/auto-transfer/monitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (monitorsResponse.ok) {
        const monitorsData = await monitorsResponse.json();
        setMonitors(monitorsData.success ? monitorsData.monitors || [] : []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGlobalSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleAddMonitor = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/monitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(addMonitorForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Auto-transfer monitor added successfully');
        setAddMonitorDialog(false);
        setAddMonitorForm({
          walletId: '',
          tokenAddress: '',
          tokenName: '',
          tokenSymbol: '',
          recipientAddress: '',
          minAmount: '1',
          maxAmount: '1000000',
          checkInterval: '5',
          enabled: true
        });
        loadData();
      } else {
        setError(data.error || 'Failed to add auto-transfer monitor');
      }
    } catch (error) {
      setError('Failed to add auto-transfer monitor');
      console.error('Error adding auto-transfer monitor:', error);
    }
  };

  const handleRemoveMonitor = async (monitorId) => {
    if (!window.confirm('Are you sure you want to remove this auto-transfer monitor?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/monitor/${monitorId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Auto-transfer monitor removed successfully');
        loadData();
      } else {
        setError(data.error || 'Failed to remove auto-transfer monitor');
      }
    } catch (error) {
      setError('Failed to remove auto-transfer monitor');
      console.error('Error removing auto-transfer monitor:', error);
    }
  };

  const handleToggleMonitor = async (monitorId, enabled) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/monitor/${monitorId}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Monitor ${enabled ? 'enabled' : 'disabled'} successfully`);
        loadData();
      } else {
        setError(data.error || 'Failed to toggle monitor');
      }
    } catch (error) {
      setError('Failed to toggle monitor');
      console.error('Error toggling monitor:', error);
    }
  };

  const handleCheckBalance = async (monitorId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/check-balance/${monitorId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Balance checked: ${data.balance} ${data.symbol}`);
        loadData();
      } else {
        setError(data.error || 'Failed to check balance');
      }
    } catch (error) {
      setError('Failed to check balance');
      console.error('Error checking balance:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auto-transfer/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(globalSettings)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Settings saved successfully');
        setSettingsDialog(false);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (error) {
      setError('Failed to save settings');
      console.error('Error saving settings:', error);
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance, decimals = 18) => {
    if (!balance) return '0';
    const num = parseFloat(balance) / Math.pow(10, decimals);
    return num.toFixed(6);
  };

  const getWalletAddress = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.address : 'Unknown';
  };

  const getStatusColor = (monitor) => {
    if (!monitor.enabled) return 'default';
    if (monitor.last_check && monitor.last_check.includes('error')) return 'error';
    if (monitor.transfers_count > 0) return 'success';
    return 'primary';
  };

  const getStatusText = (monitor) => {
    if (!monitor.enabled) return 'Disabled';
    if (monitor.transfers_count > 0) return `Active (${monitor.transfers_count} transfers)`;
    return 'Monitoring';
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 2.5 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Auto-Transfer Monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2, maxWidth: 720 }}>
        Monitor wallets for incoming tokens and automatically execute EIP-7702 transfers
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Global Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Global Settings</Typography>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setSettingsDialog(true)}
            >
              Configure
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">Check Interval</Typography>
              <Typography variant="body1">{globalSettings.globalCheckInterval}s</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">Max Retries</Typography>
              <Typography variant="body1">{globalSettings.maxRetries}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">Flashbots</Typography>
              <Typography variant="body1">{globalSettings.useFlashbots ? 'Enabled' : 'Disabled'}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">Regular TX</Typography>
              <Typography variant="body1">{globalSettings.useRegularTransaction ? 'Enabled' : 'Disabled'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monitors Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Auto-Transfer Monitors</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddMonitorDialog(true)}
            disabled={wallets.length === 0}
          >
            Add Monitor
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Wallet</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Amount Range</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Check</TableCell>
                <TableCell>Transfers</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monitors.map((monitor) => (
                <TableRow key={monitor.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatAddress(getWalletAddress(monitor.wallet_id))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {monitor.token_name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {monitor.token_symbol || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatAddress(monitor.recipient_address)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {monitor.min_amount} - {monitor.max_amount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(monitor)}
                      color={getStatusColor(monitor)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {monitor.last_check || 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {monitor.transfers_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleCheckBalance(monitor.id)}
                        disabled={loading}
                      >
                        <RefreshIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={monitor.enabled ? "warning" : "success"}
                        onClick={() => handleToggleMonitor(monitor.id, !monitor.enabled)}
                      >
                        {monitor.enabled ? <PauseIcon /> : <PlayIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMonitor(monitor.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {monitors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      {wallets.length === 0 
                        ? 'No delegated wallets available. Please delegate a wallet in the Delegation tab first.'
                        : 'No auto-transfer monitors found. Add one to get started.'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Monitor Dialog */}
      <Dialog open={addMonitorDialog} onClose={() => setAddMonitorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Auto-Transfer Monitor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Wallet</InputLabel>
                <Select
                  value={addMonitorForm.walletId}
                  onChange={(e) => setAddMonitorForm(form => ({ ...form, walletId: e.target.value }))}
                  label="Wallet"
                  disabled={wallets.length === 0}
                >
                  {wallets.length === 0 ? (
                    <MenuItem disabled>
                      No delegated wallets available. Please delegate a wallet first.
                    </MenuItem>
                  ) : (
                    wallets.map((wallet) => (
                      <MenuItem key={wallet.id} value={wallet.id}>
                        {formatAddress(wallet.address)} ({wallet.chains})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Token Address"
                value={addMonitorForm.tokenAddress}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, tokenAddress: e.target.value }))}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Token Name"
                value={addMonitorForm.tokenName}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, tokenName: e.target.value }))}
                placeholder="e.g., Wrapped Ether"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Token Symbol"
                value={addMonitorForm.tokenSymbol}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, tokenSymbol: e.target.value }))}
                placeholder="e.g., WETH"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recipient Address"
                value={addMonitorForm.recipientAddress}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, recipientAddress: e.target.value }))}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Minimum Amount"
                value={addMonitorForm.minAmount}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, minAmount: e.target.value }))}
                placeholder="1"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Maximum Amount"
                value={addMonitorForm.maxAmount}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, maxAmount: e.target.value }))}
                placeholder="1000000"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Check Interval (seconds)"
                value={addMonitorForm.checkInterval}
                onChange={(e) => setAddMonitorForm(form => ({ ...form, checkInterval: e.target.value }))}
                placeholder="5"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={addMonitorForm.enabled}
                    onChange={(e) => setAddMonitorForm(form => ({ ...form, enabled: e.target.checked }))}
                  />
                }
                label="Enable immediately"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMonitorDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMonitor} variant="contained">
            Add Monitor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Global Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Global Check Interval (seconds)"
                value={globalSettings.globalCheckInterval}
                onChange={(e) => setGlobalSettings(settings => ({ ...settings, globalCheckInterval: e.target.value }))}
                helperText="How often to check for new tokens (lower = faster detection)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Retries"
                value={globalSettings.maxRetries}
                onChange={(e) => setGlobalSettings(settings => ({ ...settings, maxRetries: e.target.value }))}
                helperText="Maximum retry attempts for failed transfers"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Retry Delay (seconds)"
                value={globalSettings.retryDelay}
                onChange={(e) => setGlobalSettings(settings => ({ ...settings, retryDelay: e.target.value }))}
                helperText="Delay between retry attempts"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Transfer Options
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={globalSettings.useFlashbots}
                    onChange={(e) => setGlobalSettings(settings => ({ ...settings, useFlashbots: e.target.checked }))}
                  />
                }
                label="Use Flashbots (MEV Protection)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={globalSettings.useRegularTransaction}
                    onChange={(e) => setGlobalSettings(settings => ({ ...settings, useRegularTransaction: e.target.checked }))}
                  />
                }
                label="Use Regular Transaction (Recommended)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AutoTransferMonitor;






